
import { useState, useEffect, useRef, useCallback } from "react";
import ChatBarWithSearch from "./chatbarwithSearch";
import GroupChatScreen from "./groupchatcomponent";
import Cookies from "js-cookie";
import { io } from "socket.io-client";

import { GroupDetails } from "./groupdetails";

export default function GroupScreen () {
  const token = Cookies.get("auth_token");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [groupList, setGroupList] = useState([]);      // 🔥 FETCHED GROUPS
  const [loading, setLoading] = useState(true);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [selectedGroupForDetails, setSelectedGroupForDetails] = useState(null);
  const socketRef = useRef(null);
  const initialGroupIdRef = useRef(new URLSearchParams(window.location.search).get("group_id"));
  useEffect(() => {
    if (window.innerWidth >= 768) setShowSidebar(true);
    fetchGroups();                                     // 🔥 CALL API ON LOAD
  }, []);

  // 🔥 LIVE GROUP LIST UPDATES (socket)
  useEffect(() => {
    if (!token) return;

    const socket = io("https://phonebookplus.app", {
      path: "/api/socket.io/",
      transports: ["polling"],
      query: { token },
      upgrade: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      });

    socket.on("group_list_updated", (payload) => {
      const group = payload?.group || payload?.data?.group || payload;
      if (!group || !group.group_id) return;

      setGroupList((prev) => {
        const idx = prev.findIndex(g => String(g.group_id) === String(group.group_id));
        const isActive = String(selectedGroupId?.group_id) === String(group.group_id);

        // If group is active, keep read (0). If not active, mark unread (1).
        const readStatus = isActive ? 0 : 1;

        const updated = idx >= 0
          ? {
              ...prev[idx],
              ...group,
              group_message_read: readStatus,
            }
          : {
              ...group,
              group_message_read: readStatus,
            };

        if (idx >= 0) {
          return [updated, ...prev.filter((_, i) => i !== idx)];
        }
        return [updated, ...prev];
      });
    });

    socket.on("disconnect", (reason) => {
      });

    return () => {
      socket.disconnect();
    };
  }, [token, selectedGroupId]);

  // ESC key handler to deselect group and return to sidebar
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && selectedGroupId) {
        setSelectedGroupId(null);
        setShowSidebar(true);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, [selectedGroupId]);

  // Sync selectedGroupId when groupList updates (after adding members)
  useEffect(() => {
    if (selectedGroupId?.group_id && groupList.length > 0) {
      const updated = groupList.find(g => g.group_id === selectedGroupId.group_id);
      if (updated) {
        setSelectedGroupId(updated);
      }
    }
  }, [groupList]);

  // Sync selectedGroupId when groupList updates
  useEffect(() => {
    if (selectedGroupId?.group_id && groupList.length > 0) {
      const updated = groupList.find(g => g.group_id === selectedGroupId.group_id);
      if (updated) {
        setSelectedGroupId(updated);
      }
    }
  }, [groupList]);

  // 🔥 API CALL FUNCTION
  const fetchGroups = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/group/list?include_all=1`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();
      const groups = data.groups || [];
      setGroupList(groups);
      return groups; // Return the fresh data
    } catch (err) {
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = useCallback((id) => {
    const selected = groupList.find((g) => g.group_id === id);
    setSelectedGroupId(selected);   // now we store full object
    // Close group details if open
    setShowGroupDetails(false);
    setSelectedGroupForDetails(null);
    if (window.innerWidth < 768) setShowSidebar(false);
  }, [groupList]);

  // Auto-open group from notification query param
  useEffect(() => {
    const groupId = initialGroupIdRef.current;
    if (!groupId || !groupList.length) return;
    const exists = groupList.some(g => String(g.group_id) === String(groupId));
    if (exists) {
      handleGroupSelect(groupId);
      // Clean query param so it doesn't re-trigger
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("group_id");
        window.history.replaceState({}, document.title, url.pathname + url.search);
      } catch (e) {
        // ignore URL cleanup errors
      }
      initialGroupIdRef.current = null;
    }
  }, [groupList, handleGroupSelect]);

  const handleGroupDetails = (id) => {
    const selected = groupList.find((g) => g.group_id === id);
    setSelectedGroupForDetails(selected);
    setShowGroupDetails(true);
    // Hide sidebar on mobile when opening details
    if (window.innerWidth < 768) setShowSidebar(false);
  };

  // Refresh group details after edit by fetching list and updating selectedGroupForDetails
  const refreshGroupDetails = async (groupId) => {
    const freshGroups = await fetchGroups();
    const updated = freshGroups.find(g => g.group_id === groupId);
    if (updated) {
      setSelectedGroupForDetails(updated);
    }
  };

  // Update group's read status when messages are marked as read
  const updateGroupReadStatus = (groupId) => {
    setGroupList(prevList => 
      prevList.map(group => 
        group.group_id === groupId 
          ? { ...group, group_message_read: 0 }
          : group
      )
    );
  };

  // Update group when new message arrives (mark as unread if not active, move to top)
  const handleGroupMessageReceived = (groupId) => {
    setGroupList((prev) => {
      const idx = prev.findIndex(g => String(g.group_id) === String(groupId));
      if (idx < 0) return prev;
      
      const isActive = String(selectedGroupId?.group_id) === String(groupId);
      const updated = {
        ...prev[idx],
        group_message_read: isActive ? 0 : 1, // 0 if active (read), 1 if not active (unread)
      };
      
      // Move to top
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  };

  const handleBackMobile = () => setShowSidebar(true);
  return (
    <div className="h-full min-h-0 overflow-hidden bg-[#F3F0FF]">
      <div className="h-full min-h-0 rounded-2xl shadow-lg overflow-hidden">
        <div className="flex h-full min-h-0">

          {/* LEFT SIDEBAR - Show on mobile only when showSidebar is true, always on desktop */}
            <div className={`${showSidebar ? "block" : "hidden"} md:block w-full md:w-[360px] md:min-w-[300px] md:flex-shrink-0 h-full min-h-0`}>
            <ChatBarWithSearch
              onSelectContact={handleGroupSelect}
              onSelectGroupDetails={handleGroupDetails}
              onGroupMoved={fetchGroups}
              contacts={groupList}      // 🔥 REPLACE STATIC WITH API DATA
              selectedId={selectedGroupId}
              loading={loading}
            />
          </div>

          {/* GROUP CHAT AREA / GROUP DETAILS - Show on mobile only when group selected, always on desktop */}
            <div className={`${showSidebar ? "hidden" : "block"} md:block w-full md:flex-1 h-full min-h-0 overflow-hidden`}>
            {showGroupDetails && selectedGroupForDetails ? (
              <GroupDetails 
                groupData={selectedGroupForDetails} 
                fetchGroups={fetchGroups}
                refreshGroupDetails={refreshGroupDetails}
                handleGroupDetails={handleGroupDetails}
                onBack={() => {
                  setShowGroupDetails(false);
                  setSelectedGroupForDetails(null);
                  if (window.innerWidth < 768) setShowSidebar(true);
                }}
              />
            ) : selectedGroupId ? (
              <GroupChatScreen 
                groupId={selectedGroupId.group_id} 
                onBackMobile={handleBackMobile} 
                groupList={groupList}
                onMarkAsRead={updateGroupReadStatus}
                onMessageReceived={handleGroupMessageReceived}
                onHeaderClick={handleGroupDetails}
              />
            ) : (
              <div className="hidden md:flex flex-col items-center justify-center h-full gap-4 border-gray-200 border bg-[#F4F3FF]">
                <div className="h-40 w-40 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
                  <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
                </div>

                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
                   PhoneBookPlus
                </h1>
                
                <p className="text-gray-400">Select a contact to start chatting</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
